import {
    Injectable, inject
} from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    BulkResponse,
    Role,
    RoleSearchFilterOptions
} from "@shopliftr/common-js/shared";
import { StringUtil } from "@shopliftr/common-js/data";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { RoleApiClientService } from "../../iapi-application/services/api-client/role/role-api-client.service";
import { RoleAutocompleteControllerOptions } from "./role-autocomplete-controller-options";

/**
 * Autocomplete controller service for Role selection.
 *
 * This service implements the IAutocompleteController interface to provide
 * autocomplete functionality for roles using the RoleApiClientService.
 * Supports two modes of operation:
 * - When role IDs are provided in options, fetches only those specific roles via bulkGet$
 * - When no IDs are provided, fetches all roles via getAll$
 * Results are filtered client-side by role name.
 *
 * Usage:
 * ```typescript
 * constructor() {
 *   private readonly roleController = inject(RoleAutocompleteControllerService);
 * }
 *
 * // Fetch specific roles
 * searchRoles() {
 *   this.roleController.autocomplete$("admin", { ids: ["role-1", "role-2"] })
 *     .subscribe(results => console.log(results.options));
 * }
 *
 * // Fetch all roles
 * searchAllRoles() {
 *   this.roleController.autocomplete$("admin")
 *     .subscribe(results => console.log(results.options));
 * }
 * ```
 */
@Injectable({ providedIn: "root" })
export class RoleAutocompleteControllerService implements IAutocompleteController<Role, RoleAutocompleteControllerOptions> {

    private readonly _roleApiClientService = inject(RoleApiClientService);

    /**
     * Execute an autocomplete request for roles
     *
     * @param query - The text query used to filter roles by name (client-side)
     * @param options - Optional configuration including specific role IDs to fetch and sort order
     * @returns Observable of autocomplete results containing filtered and sorted roles
     */
    autocomplete$(query?: string, options?: RoleAutocompleteControllerOptions): Observable<IAutocompleteResults<Role>> {
        const sanitizedQuery = StringUtil.sanitizeString(query ?? "")?.toLowerCase();

        // When ids provided → bulkGet$, otherwise → getAll$
        let roles$: Observable<Array<Role>>;
        if (options?.ids?.length) {
            roles$ = this._roleApiClientService.bulkGet$(options.ids).pipe(
                map((response: BulkResponse) => response.results
                    .filter((item) => item.payload !== null && item.payload !== undefined)
                    .map((item) => item.payload as unknown as Role))
            );
        }
        else {
            roles$ = this._roleApiClientService.getAll$(new RoleSearchFilterOptions());
        }

        return roles$.pipe(
            map((roles: Array<Role>) => {
                let filtered = roles;

                // Client-side filter by name
                if (!StringUtil.emptyString(sanitizedQuery)) {
                    filtered = roles.filter((role) =>
                        role.name?.toLowerCase().includes(sanitizedQuery));
                }

                // Sort alphabetically by name
                filtered.sort((a, b) => {
                    const nameA = a.name?.toLowerCase() || "";
                    const nameB = b.name?.toLowerCase() || "";
                    return nameA.localeCompare(nameB);
                });

                return {
                    options: filtered.map((role) => ({
                        option: role.name,
                        value: role
                    }))
                };
            })
        );
    }

    /**
     * Returns the display name for a role value.
     * For roles, the display name is the role's name property.
     *
     * @param value - The role object
     * @param options - Additional options (not used for roles)
     * @returns The display name for the role
     */
    getAutocompleteDisplayName(value?: Role, options?: RoleAutocompleteControllerOptions): string {
        return value?.name || "";
    }

    /**
     * Gets the identifier for tracking purposes in autocomplete lists.
     * Uses the role ID as the unique identifier.
     *
     * @param index - The index of the item in the list
     * @param result - The autocomplete option result
     * @returns The unique identifier for the role
     */
    trackByIdentifier(index: number, result?: IAutocompleteOption<Role>): any {
        return result?.value?.id;
    }

    /**
     * Determines if the role is unique compared to the roles in the array
     *
     * @param value - The role to check
     * @param items - Array of existing roles
     * @returns True if the role is not in the items array, false otherwise
     */
    isUnique(value: Role, items: Array<Role>): boolean {
        return !items?.some((item) => item.id === value?.id);
    }
}
